import { addDays, format, isSameDay, isToday, startOfWeek } from "date-fns";
import { ja } from "date-fns/locale";
import { formatWeekRange } from "../lib/week-range";
import { useDateStore } from "../stores/dateStore";
import { Button } from "./ui/Button";

function ChevronLeftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface DayButtonProps {
  date: Date;
  isSelected: boolean;
  onClick: () => void;
}

function DayButton({ date, isSelected, onClick }: DayButtonProps) {
  const today = isToday(date);

  const baseClasses =
    "flex flex-col items-center justify-center py-3 px-1 sm:px-3 rounded-lg transition-colors min-w-[44px] sm:min-w-[52px]";

  let stateClasses: string;
  if (isSelected) {
    stateClasses = "bg-primary-600 text-white cursor-pointer";
  } else if (today) {
    stateClasses =
      "bg-primary-100 border-2 border-primary-500 text-primary-700 cursor-pointer";
  } else {
    stateClasses =
      "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 cursor-pointer";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} ${stateClasses}`}
      aria-current={today ? "date" : undefined}
      aria-selected={isSelected}
    >
      <span className="text-xs font-medium">
        {format(date, "E", { locale: ja })}
      </span>
      <span className="text-lg font-semibold">{format(date, "d")}</span>
      {today && !isSelected && (
        <span className="text-[10px] text-primary-600">今日</span>
      )}
    </button>
  );
}

export function DateSelector() {
  const {
    selectedDate,
    setSelectedDate,
    goToToday,
    goToPreviousWeek,
    goToNextWeek,
  } = useDateStore();

  // 週の開始日（月曜日）を取得
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });

  // 平日（月〜金）のみ生成
  const weekDays: Date[] = [];
  for (let i = 0; i < 5; i++) {
    weekDays.push(addDays(weekStart, i));
  }

  // 週の表示範囲テキスト（平日のみ、曜日付き）
  const weekRangeText = formatWeekRange(weekStart);

  const isTodayInCurrentWeek = weekDays.some((day) => isToday(day));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      {/* ナビゲーションヘッダー */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPreviousWeek}
            aria-label="前の週"
          >
            <ChevronLeftIcon />
          </Button>
          <span className="text-sm font-medium text-gray-700 min-w-[200px] text-center">
            {weekRangeText}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextWeek}
            aria-label="次の週"
          >
            <ChevronRightIcon />
          </Button>
        </div>
        {!isTodayInCurrentWeek && (
          <Button variant="secondary" size="sm" onClick={goToToday}>
            今日
          </Button>
        )}
      </div>

      {/* 週の日付ボタン */}
      <div
        className="flex gap-1 sm:gap-2 justify-between"
        role="group"
        aria-label="日付選択"
      >
        {weekDays.map((day) => (
          <DayButton
            key={day.toISOString()}
            date={day}
            isSelected={isSameDay(day, selectedDate)}
            onClick={() => setSelectedDate(day)}
          />
        ))}
      </div>
    </div>
  );
}
